import { useCallback, useEffect, useRef } from 'react'
import type { GridColumnState } from './types'

const STORAGE_PREFIX = 'grid-column-state:'

/**
 * ag-grid persisted its own `ColumnState[]` under the page's own key. Those
 * payloads are read here unchanged so a user's saved layout survives the
 * migration — the shapes overlap on the fields we care about
 * ({ colId, width, hide, pinned }), and anything else ag wrote is ignored.
 */
export const parseColumnState = (raw: string | null): GridColumnState[] | null => {
  if (!raw) return null

  try {
    const parsed = JSON.parse(raw)
    const list = Array.isArray(parsed) ? parsed : parsed?.columnState
    if (!Array.isArray(list)) return null

    const state = list
      .filter((entry) => entry && typeof entry.colId === 'string')
      .map((entry) => ({
        colId: String(entry.colId),
        ...(typeof entry.width === 'number' ? { width: entry.width } : {}),
        ...(typeof entry.hide === 'boolean' ? { hide: entry.hide } : {}),
        ...(entry.pinned === 'left' || entry.pinned === 'right'
          ? { pinned: entry.pinned as 'left' | 'right' }
          : entry.pinned === null
            ? { pinned: null }
            : {}),
      }))

    return state.length > 0 ? state : null
  } catch {
    return null
  }
}

interface UseColumnStatePersistenceArgs {
  storageKey?: string
  /** Extra keys to read when `storageKey` has nothing stored — old ag-grid keys. */
  legacyKeys?: string[]
  /**
   * Use `storageKey` verbatim rather than namespacing it. Set by pages that
   * already own a key an earlier build wrote to, so a layout saved before the
   * migration keeps being read *and* written in place.
   */
  unprefixed?: boolean
  onRestore: (state: GridColumnState[]) => void
}

export const useColumnStatePersistence = ({
  storageKey,
  legacyKeys,
  unprefixed = false,
  onRestore,
}: UseColumnStatePersistenceArgs) => {
  const restoredRef = useRef(false)
  const onRestoreRef = useRef(onRestore)
  onRestoreRef.current = onRestore

  const writeKey = storageKey
    ? unprefixed
      ? storageKey
      : STORAGE_PREFIX + storageKey
    : undefined

  useEffect(() => {
    if (!writeKey || !storageKey || restoredRef.current) return
    restoredRef.current = true

    let state: GridColumnState[] | null = null
    try {
      state = parseColumnState(localStorage.getItem(writeKey))

      // Fall back to any layout the ag-grid build wrote before the migration.
      if (!state) {
        for (const key of [storageKey, ...(legacyKeys ?? [])]) {
          state = parseColumnState(localStorage.getItem(key))
          if (state) break
        }
      }
    } catch {
      // Private browsing / storage disabled — start from the column defs.
      return
    }

    if (state) onRestoreRef.current(state)
  }, [writeKey, storageKey, legacyKeys])

  const persist = useCallback(
    (state: GridColumnState[]) => {
      if (!writeKey) return
      try {
        localStorage.setItem(writeKey, JSON.stringify(state))
      } catch {
        // Storage full or disabled: the layout just will not persist.
      }
    },
    [writeKey],
  )

  return { persist }
}
